import React, { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface SalesOverview {
  totalOrders: number;
  totalRevenue: number;
  totalSales: number;
  totalProfit: number;
  commissionsOwed: number;
  commissionsPaid: number;
}

interface SalesByRep {
  salesRepId: string;
  firstName: string;
  lastName: string;
  name: string;
  role: string;
  orderCount: number;
  revenue: number;
  sales: number;
  profit: number;
  commissionOwed: number;
  commissionPaid: number;
}

async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
}

const roleLabels: Record<string, string> = {
  sales_rep: 'Sales Rep',
  sales_manager: 'Sales Manager',
};

const roleColors: Record<string, string> = {
  sales_rep: 'bg-blue-900/50 text-blue-300',
  sales_manager: 'bg-purple-900/50 text-purple-300',
};

type DateRange = 'this_month' | 'last_month' | 'this_quarter' | 'ytd' | 'custom';

function getDateRange(range: DateRange): { startDate: string; endDate: string } {
  const now = new Date();
  let startDate: Date;
  let endDate: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

  switch (range) {
    case 'this_month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'last_month':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      break;
    case 'this_quarter':
      const quarter = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), quarter * 3, 1);
      break;
    case 'ytd':
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
}

export function SalesOverviewPage() {
  const [overview, setOverview] = useState<SalesOverview | null>(null);
  const [salesByRep, setSalesByRep] = useState<SalesByRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [dateRange, customStartDate, customEndDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      let params: { startDate: string; endDate: string };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params = { startDate: customStartDate, endDate: customEndDate };
      } else {
        params = getDateRange(dateRange);
      }

      const queryString = `?startDate=${params.startDate}&endDate=${params.endDate}`;

      const [overviewData, byRepData] = await Promise.all([
        apiRequest(`/dealer-order-commissions/sales-overview${queryString}`),
        apiRequest(`/dealer-order-commissions/sales-by-rep${queryString}`),
      ]);

      setOverview(overviewData);
      setSalesByRep(byRepData || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load sales data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const exportToCSV = () => {
    if (salesByRep.length === 0) return;

    setExporting(true);

    const headers = ['Rep Name', 'Role', 'Orders', 'Revenue', 'Sales', 'Profit', 'Commission Owed', 'Commission Paid'];
    const rows = salesByRep.map(rep => [
      rep.name,
      roleLabels[rep.role] || rep.role,
      rep.orderCount.toString(),
      rep.revenue.toFixed(2),
      rep.sales.toFixed(2),
      rep.profit.toFixed(2),
      rep.commissionOwed.toFixed(2),
      rep.commissionPaid.toFixed(2),
    ]);

    // Add totals row
    const totals = salesByRep.reduce(
      (acc, rep) => ({
        orders: acc.orders + rep.orderCount,
        revenue: acc.revenue + rep.revenue,
        sales: acc.sales + rep.sales,
        profit: acc.profit + rep.profit,
        commissionOwed: acc.commissionOwed + rep.commissionOwed,
        commissionPaid: acc.commissionPaid + rep.commissionPaid,
      }),
      { orders: 0, revenue: 0, sales: 0, profit: 0, commissionOwed: 0, commissionPaid: 0 }
    );

    rows.push([
      'TOTAL',
      '',
      totals.orders.toString(),
      totals.revenue.toFixed(2),
      totals.sales.toFixed(2),
      totals.profit.toFixed(2),
      totals.commissionOwed.toFixed(2),
      totals.commissionPaid.toFixed(2),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sales-overview-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    setExporting(false);
  };

  // Calculate totals for the table footer
  const totals = salesByRep.reduce(
    (acc, rep) => ({
      orders: acc.orders + rep.orderCount,
      revenue: acc.revenue + rep.revenue,
      sales: acc.sales + rep.sales,
      profit: acc.profit + rep.profit,
      commissionOwed: acc.commissionOwed + rep.commissionOwed,
      commissionPaid: acc.commissionPaid + rep.commissionPaid,
    }),
    { orders: 0, revenue: 0, sales: 0, profit: 0, commissionOwed: 0, commissionPaid: 0 }
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Sales Overview</h1>
          <p className="text-gray-400 mt-1">View sales performance and commission data</p>
        </div>
        <button
          onClick={exportToCSV}
          disabled={exporting || salesByRep.length === 0}
          className="bg-keder-gray hover:bg-keder-gray/80 text-white font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export to CSV
        </button>
      </div>

      {/* Date Range Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <label className="text-sm font-medium text-gray-400">Date Range:</label>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'this_month', label: 'This Month' },
              { value: 'last_month', label: 'Last Month' },
              { value: 'this_quarter', label: 'This Quarter' },
              { value: 'ytd', label: 'YTD' },
              { value: 'custom', label: 'Custom' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setDateRange(option.value as DateRange)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === option.value
                    ? 'bg-keder-yellow text-black'
                    : 'bg-keder-gray/50 text-gray-300 hover:bg-keder-gray'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {dateRange === 'custom' && (
            <div className="flex items-center gap-2 mt-2 sm:mt-0">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="input-field py-1.5 px-3 text-sm"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="input-field py-1.5 px-3 text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="card bg-red-900/20 border-red-800">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-keder-gray border-t-keder-yellow"></div>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <div className="card">
              <p className="text-sm text-gray-400">Total Orders</p>
              <p className="text-2xl font-bold text-white mt-1">{overview?.totalOrders || 0}</p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-400">Total Revenue</p>
              <p className="text-2xl font-bold text-white mt-1">{formatCurrency(overview?.totalRevenue || 0)}</p>
              <p className="text-xs text-gray-500 mt-1">What you paid Keder</p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-400">Total Sales</p>
              <p className="text-2xl font-bold text-white mt-1">{formatCurrency(overview?.totalSales || 0)}</p>
              <p className="text-xs text-gray-500 mt-1">What customers paid you</p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-400">Total Profit</p>
              <p className={`text-2xl font-bold mt-1 ${(overview?.totalProfit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(overview?.totalProfit || 0)}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-400">Commissions Owed</p>
              <p className="text-2xl font-bold text-keder-yellow mt-1">{formatCurrency(overview?.commissionsOwed || 0)}</p>
              <p className="text-xs text-gray-500 mt-1">Pending + Earned</p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-400">Commissions Paid</p>
              <p className="text-2xl font-bold text-green-400 mt-1">{formatCurrency(overview?.commissionsPaid || 0)}</p>
            </div>
          </div>

          {/* Disclaimer Banner */}
          <div className="bg-keder-gray/30 border border-keder-gray rounded-lg px-4 py-3">
            <p className="text-sm text-gray-400">
              Commission tracking is a convenience tool. Keder Screens does not pay or verify commissions.
            </p>
          </div>

          {/* Sales by Rep Table */}
          <div className="card overflow-hidden p-0">
            <div className="px-6 py-4 border-b border-keder-gray">
              <h2 className="text-lg font-semibold text-white">Sales by Rep</h2>
            </div>

            {salesByRep.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400">No sales data found for the selected period.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-keder-gray">
                  <thead className="bg-keder-gray/30">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Rep Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Orders
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Revenue
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Sales
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Profit
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Comm. Owed
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Comm. Paid
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-keder-gray/50">
                    {salesByRep.map((rep) => (
                      <tr key={rep.salesRepId} className="hover:bg-keder-gray/20">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-white">{rep.name}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleColors[rep.role] || 'bg-gray-600 text-white'}`}>
                            {roleLabels[rep.role] || rep.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-white">
                          {rep.orderCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-white">
                          {formatCurrency(rep.revenue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-white">
                          {formatCurrency(rep.sales)}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-right font-medium ${rep.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatCurrency(rep.profit)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-keder-yellow">
                          {formatCurrency(rep.commissionOwed)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-green-400">
                          {formatCurrency(rep.commissionPaid)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-keder-gray/50">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-bold text-white">TOTAL</td>
                      <td className="px-6 py-4"></td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-white">
                        {totals.orders}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-white">
                        {formatCurrency(totals.revenue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-white">
                        {formatCurrency(totals.sales)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-right font-bold ${totals.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(totals.profit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-keder-yellow">
                        {formatCurrency(totals.commissionOwed)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-green-400">
                        {formatCurrency(totals.commissionPaid)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default SalesOverviewPage;
